import type { IconName } from '@models/IIcon';
import classNames from 'classnames';

export interface ButtonStyleProps {
  readonly className?: string;
  readonly disabled?: boolean;
  readonly hasCustomStyling?: boolean;
  readonly icon?: IconName;
  readonly interactionTheme?: 'grey-250' | 'grey-600';
  readonly isLightMode?: boolean;
  readonly secondary?: boolean;
  readonly secondaryBackgroundClassName?: string;
  readonly theme?: 'yellow' | 'black' | 'red';
}

export const getButtonClasses = ({
  className,
  disabled = false,
  hasCustomStyling,
  icon,
  interactionTheme = 'grey-600',
  isLightMode,
  secondary = false,
  secondaryBackgroundClassName = 'bg-transparent',
  theme = 'yellow',
}: ButtonStyleProps): string => {
  const isYellow = theme === 'yellow';
  const isBlack = theme === 'black';
  const isRed = theme === 'red';

  const interactionThemeClasses: Record<'grey-250' | 'grey-600', string> = {
    'grey-250': 'hover:bg-grey-250 focus:bg-grey-250 active:bg-grey-250',
    'grey-600': 'hover:bg-grey-600 focus:bg-grey-600 active:bg-grey-600',
  };

  return classNames(
    {
      // Yellow primary
      'bg-yellow py-1 px-4 text-black-100 hover:bg-yellow-100 focus:bg-yellow-500 active:bg-yellow-500':
        !hasCustomStyling && !disabled && !secondary && isYellow,

      // Yellow secondary
      [`${secondaryBackgroundClassName} border border-yellow py-[3px] px-[15px] text-black-100 hover:border-yellow-100 hover:bg-yellow-100 hover:text-black-100 focus:border-yellow-500 focus:bg-yellow-500 active:border-yellow-500 active:bg-yellow-500 ${
        isLightMode
          ? ''
          : 'dark:text-white dark:hover:text-black-100 dark:focus:text-black-100 dark:active:text-black-100'
      }`]: !hasCustomStyling && !disabled && secondary && isYellow,

      // Black primary
      [`bg-black-100 py-1 px-4 text-white ${interactionThemeClasses[interactionTheme]}`]:
        !hasCustomStyling && !disabled && !secondary && isBlack,

      // Black secondary
      'border border-black-100 bg-transparent px-[15px] py-[3px] text-black-100 hover:bg-black-100 hover:text-white focus:bg-black-100 focus:text-white active:bg-black-100 active:text-white':
        !hasCustomStyling && !disabled && secondary && isBlack,

      // Red secondary
      'border border-red-100 bg-transparent px-[15px] py-[3px] text-black-100 hover:bg-red-100 hover:text-white focus:bg-red-100 focus:text-white active:bg-red-100 active:text-white':
        !hasCustomStyling && !disabled && secondary && isRed,

      // Disabled
      'bg-grey text-grey-500 py-1 px-4': disabled,

      // With Icon
      'flex items-center w-fit group': icon,
    },
    'cta rounded-full inline-block min-h-min select-none',
    className
  );
};
